// Chalk Explainer Ad's storyboard stage (live's `storyboard-stage`, `chalk-still-card`,
// `clip-cues-row` and `board-spec-summary`; not captured, live's stage needs a paid Approve
// script, so built from live's code, spec G14-chalk-explainer-ad PR d2): "Storyboard" with Back
// to script, the five images each with its ↻, each clip's cues with their planned times, the cue
// warnings and the board spec.
import { Button, Group, Stack, Text } from "@mantine/core";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";

import type {
  ChalkBoard,
  ChalkClipCues,
  ChalkDraftStill,
  ChalkDraftStillName,
} from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { FieldLabel } from "../../../ui/FieldLabel";
import { ChalkDot } from "./ChalkScript";
import { StillCard } from "./StillCard";
import { STILL_NAMES, stillWaiting } from "./chalk-draft";
import { clipTitle, warningLine } from "./chalk-script";
import chalk from "./chalk.module.css";
import classes from "./podcast.module.css";

/** Live's words (`constants.ts`). */
export const STILL_LABELS: Readonly<Record<ChalkDraftStillName, string>> = {
  board0: "Start of A",
  end_a: "A → B",
  end_b: "B → C",
  end_c: "End of C",
  end_card: "End card",
};
const STORYBOARD_TOOLTIP =
  "Four photos of the board — each clip starts on one and ends on the next — and the end card. ↻ " +
  "redraws an image and every image built on it. The times are when each element must be finished.";
export const BACK_TO_SCRIPT = "Back to script";
const BACK_TO_SCRIPT_CONFIRM =
  "Edit the script again? The board and the images will be made again after you approve it.";
const META = " · ";
const PENDING = { status: "pending", url: null, problem: null } as const;

/** A cue as live's row shows it (`toCueLabel`): ⚠ where warned, its label, its planned time. */
export function cueLabel(label: string, seconds: number | null, warned: boolean): string {
  return `${warned ? "⚠ " : ""}${label}${seconds === null ? "" : ` ${seconds} s`}`;
}

/** The board spec's blocks (live's `toSpecBlocks`): the product as held, then each clip's beats
 * and where the hand ends. */
export function specBlocks(board: ChalkBoard): { title: string; lines: string[] }[] {
  return [
    { title: "Product", lines: [board.product.description, [board.product.grip, board.product.size_line].join(META)] },
    ...board.clips.map((clip) => ({
      title: clipTitle(clip.clip),
      lines: [
        ...clip.beats.map((beat) => `${beat.line_id}: ${beat.action}`),
        ...(clip.hand_end ? [`hand ends: ${clip.hand_end}`] : []),
      ],
    })),
  ];
}

function stillItem(still: ChalkDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface ChalkStoryboardProps {
  stills: readonly ChalkDraftStill[];
  board: ChalkBoard | null;
  cues: readonly ChalkClipCues[];
  cueWarnings: readonly string[];
  /** The board director writes the board: the three board images wait on it. */
  boarding: boolean;
  busy: boolean;
  resetting: boolean;
  onRegenerate: (name: ChalkDraftStillName) => void;
  onBackToScript: () => void;
  onStartOver: () => void;
}

/**
 * Live's storyboard: its title and Back to script (live's confirm first), the five images in
 * live's order, a done one opening in the shared lightbox; under them each clip's cues, the cue
 * warnings and the board spec, folded. Ours also offers "Start over", as Pixar Talking Ad's (the
 * draft outlives the panel).
 */
export function ChalkStoryboard(props: ChalkStoryboardProps) {
  const { stills, board, cues, cueWarnings, boarding, busy, resetting } = props;
  const [preview, setPreview] = useState<ChalkDraftStillName | null>(null);
  const [confirming, setConfirming] = useState<"back" | "over" | null>(null);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label="Storyboard" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text component="div" className={classes.castTitle}>
          <FieldLabel label="Storyboard" info={STORYBOARD_TOOLTIP} spoken />
        </Text>
        <Group gap={4} wrap="nowrap">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<ArrowLeft size={14} aria-hidden />}
            disabled={busy}
            onClick={() => setConfirming("back")}
          >
            {BACK_TO_SCRIPT}
          </Button>
          <Button variant="subtle" size="compact-sm" disabled={resetting || busy} onClick={() => setConfirming("over")}>
            Start over
          </Button>
        </Group>
      </Group>
      <div className={classes.stills}>
        {STILL_NAMES.map((name) => (
          <StillCard
            key={name}
            label={STILL_LABELS[name]}
            still={stills.find((each) => each.name === name) ?? PENDING}
            waiting={stillWaiting(name, stills, boarding)}
            busy={busy}
            onRegenerate={() => props.onRegenerate(name)}
            onFixLabel={null}
            onPreview={() => setPreview(name)}
          />
        ))}
      </div>
      {cues.length === 0 ? null : (
        <Stack gap={4}>
          {cues.map((entry) => (
            <div key={entry.clip} className={chalk.row}>
              <Text className={chalk.cuesTitle}>{`${clipTitle(entry.clip)}${META}${entry.duration_seconds} s`}</Text>
              <div className={chalk.cues}>
                {entry.cues.map((cue) => (
                  <span key={`${cue.line_id}${cue.label}`} className={chalk.cue} data-warned={cue.warned || undefined}>
                    <ChalkDot color={cue.color} />
                    {cueLabel(cue.label, cue.planned_seconds, cue.warned)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Stack>
      )}
      {cueWarnings.length === 0 ? null : (
        <div className={chalk.warnings}>
          {cueWarnings.map((warning) => (
            <Text key={warning}>{warningLine(warning)}</Text>
          ))}
        </div>
      )}
      {board === null ? null : (
        <details className={chalk.spec}>
          <summary>Board spec</summary>
          <Stack gap={8}>
            {specBlocks(board).map((block) => (
              <Stack key={block.title} gap={0}>
                <Text className={chalk.specTitle}>{block.title}</Text>
                {block.lines.map((line) => (
                  <Text key={line} className={chalk.specLine}>
                    {line}
                  </Text>
                ))}
              </Stack>
            ))}
          </Stack>
        </details>
      )}
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as ChalkDraftStillName | null)}
      />
      <ConfirmModal
        opened={confirming !== null}
        title={confirming === "back" ? BACK_TO_SCRIPT : "Start over"}
        confirmLabel={confirming === "back" ? BACK_TO_SCRIPT : "Start over"}
        pending={resetting}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming === "back") props.onBackToScript();
          else props.onStartOver();
          setConfirming(null);
        }}
      >
        <Text size="sm">
          {confirming === "back"
            ? BACK_TO_SCRIPT_CONFIRM
            : "The script, the board and the five images are cleared, and the form starts empty."}
        </Text>
      </ConfirmModal>
    </Stack>
  );
}
